import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Zap, Crown, Check, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';




// Helper component: Pricing Card
const PricingCard = ({ tier, icon: Icon, name, monthly, annual, features, selected, onSelect, popular }) => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const tierColors = {
    standard: 'from-blue-400 to-blue-600',
    premium: 'from-purple-400 to-purple-600',
    gold: 'from-yellow-400 to-yellow-600'
  };

  const borderColors = {
    standard: 'border-blue-500',
    premium: 'border-purple-500',
    gold: 'border-yellow-500'
  };

  return (
    <div
      className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${selected ? borderColors[tier] + ' bg-gray-50' : 'border-gray-200 hover:border-gray-300'
        }`}
      onClick={onSelect}
    >
      {popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-bold">
            MOST POPULAR
          </span>
        </div>
      )}

      <div className="flex items-center justify-center mb-4">
        <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${tierColors[tier]} flex items-center justify-center`}>
          <Icon className="w-8 h-8 text-white" />
        </div>
      </div>

      <h3 className="text-2xl font-bold text-center mb-2">{name}</h3>

      <div className="text-center mb-4">
        <span className="text-4xl font-bold">GH₵{billingCycle === 'monthly' ? monthly : annual}</span>
        <span className="text-gray-600">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
      </div>

      {billingCycle === 'annual' && (
        <p className="text-sm text-green-600 text-center mb-4">
          Save GH₵{(monthly * 12) - annual} per year!
        </p>
      )}

      <ul className="space-y-3 mb-6">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Helper component: Gig/Request Selector
const GigRequestSelector = ({ type, items, selected, onSelect }) => {
  if (!items || items.length === 0) {
    return (
      <div className="bg-gray-100 rounded-lg p-8 text-center mb-8">
        <p className="text-gray-600">You don't have any active {type}s to feature.</p>
        <a href={type === 'gig' ? '/gigs/create' : '/requests/create'} className="text-blue-600 hover:underline mt-2 inline-block">
          Create your first {type}
        </a>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold mb-4">Select {type} to feature:</h3>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map(item => (
          <div
            key={item.id}
            onClick={() => onSelect(item)}
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${selected?.id === item.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
          >
            <div className="flex gap-3">
              <img
                src={item.images?.[0]?.url || item.primaryImage || '/api/placeholder/80/80'}
                alt={item.title}
                className="w-20 h-20 object-cover rounded"
              />
              <div className="flex-1">
                <h4 className="font-semibold line-clamp-2">{item.title}</h4>
                <p className="text-sm text-gray-600 mt-1">
                  {item.category?.name || (typeof item.category === 'string' ? item.category : 'Uncategorized')}
                </p>
                {selected?.id === item.id && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-medium">
                      <Check className="w-3 h-3 mr-1" /> Selected
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FeaturedSubscription = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedTier, setSelectedTier] = useState('standard');
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [targetType, setTargetType] = useState('profile');
  const [myGigs, setMyGigs] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [pricingConfig, setPricingConfig] = useState(null);

  useEffect(() => {
    checkFeatureStatus();
    if (user) {
      fetchMyGigs();
      fetchMyRequests();
    }
  }, [user]);

  useEffect(() => {
    // Reset selected target when changing target type
    setSelectedTarget(null);
  }, [targetType]);

  const checkFeatureStatus = async () => {
    try {
      const token = localStorage.getItem('gigship_token');
      const response = await axios.get(`${API_BASE_URL}/featured/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setFeatureEnabled(response.data.enabled);
      setPricingConfig(response.data.pricingTiers);
    } catch (error) {
      console.error('Failed to check feature status:', error);
      setFeatureEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyGigs = async () => {
    try {
      const token = localStorage.getItem('gigship_token');
      const response = await axios.get(`${API_BASE_URL}/gigs/my-gigs`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMyGigs(response.data.data?.filter(g => g.status === 'active') || []);
    } catch (error) {
      console.error('Failed to fetch gigs:', error);
    }
  };

  const fetchMyRequests = async () => {
    try {
      const token = localStorage.getItem('gigship_token');
      const response = await axios.get(`${API_BASE_URL}/job-requests/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const raw = response.data.data;
      const list = Array.isArray(raw) ? raw : (raw?.requests || []);
      setMyRequests(list.filter(r => r.status === 'open'));
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
  };

  const getPricing = () => {
    if (!pricingConfig) return 0;
    return pricingConfig[selectedTier]?.[selectedPlan] || 0;
  };

  const handlePurchase = async () => {
    // Validation
    if (targetType !== 'profile' && !selectedTarget) {
      toast.error(`Please select a ${targetType} to feature`);
      return;
    }

    setPurchasing(true);

    try {
      const token = localStorage.getItem('gigship_token');
      const payload = {
        addon: targetType === 'gig' ? 'featured_gig' : targetType === 'request' ? 'featured_request' : 'featured_profile',
        plan: selectedPlan,
        tier: selectedTier,
        targetType: targetType === 'gig' ? 'Gig' : targetType === 'request' ? 'Request' : 'User',
        targetId: targetType === 'profile' ? user.id : selectedTarget.id,
        paymentMethod: 'wallet' // Default to wallet, can add card option
      };

      const response = await axios.post(
        `${API_BASE_URL}/featured/subscribe`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Featured subscription activated!');
        navigate('/dashboard/subscriptions');
      }
    } catch (error) {
      console.error('Purchase failed:', error);
      toast.error(error.response?.data?.message || 'Failed to activate featured subscription');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!featureEnabled) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="bg-gray-100 rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">🚀</div>
          <h2 className="text-2xl font-bold mb-4">Featured Listings</h2>
          <p className="text-gray-600 mb-4">
            This premium feature is coming soon! Get ready to boost your visibility and stand out from the crowd.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Get Featured & Stand Out</h1>
        <p className="text-gray-600 text-lg">Boost your visibility and get more orders</p>
      </div>
      {/* Target Type Selector */}
      <div className="flex justify-center gap-4 mb-8">
        <button
          onClick={() => setTargetType('profile')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${targetType === 'profile'
            ? 'bg-blue-600 text-white shadow-lg'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
        >
          Feature My Profile
        </button>
        <button
          onClick={() => setTargetType('gig')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${targetType === 'gig'
            ? 'bg-blue-600 text-white shadow-lg'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
        >
          Feature a Gig
        </button>
        <button
          onClick={() => setTargetType('request')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${targetType === 'request'
            ? 'bg-blue-600 text-white shadow-lg'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
        >
          Feature a Request
        </button>
      </div>
      {/* Plan Selector */}
      <div className="flex justify-center gap-4 mb-12">
        <button
          onClick={() => setSelectedPlan('monthly')}
          className={`px-8 py-3 rounded-lg font-medium transition-colors ${selectedPlan === 'monthly'
            ? 'bg-blue-600 text-white shadow-lg'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setSelectedPlan('annual')}
          className={`px-8 py-3 rounded-lg font-medium transition-colors relative ${selectedPlan === 'annual'
            ? 'bg-blue-600 text-white shadow-lg'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
        >
          Annual
          <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
            Save 17%
          </span>
        </button>
      </div>
      {/* Pricing Tiers */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <PricingCard
          tier="standard"
          icon={Star}
          name="Standard"
          monthly={pricingConfig?.standard?.monthly || 50}
          annual={pricingConfig?.standard?.annual || 500}
          features={[
            '🔵 Blue featured badge',
            '📈 1.5x search visibility',
            '🤖 +10 AI matching bonus',
            '📊 Basic analytics'
          ]}
          selected={selectedTier === 'standard'}
          onSelect={() => setSelectedTier('standard')}
        />
        <PricingCard
          tier="premium"
          icon={Zap}
          name="Premium"
          monthly={pricingConfig?.premium?.monthly || 100}
          annual={pricingConfig?.premium?.annual || 1000}
          features={[
            '💜 Purple featured badge',
            '📈 2x search visibility',
            '🤖 +20 AI matching bonus',
            '📊 Advanced analytics',
            '🎯 Priority support'
          ]}
          selected={selectedTier === 'premium'}
          onSelect={() => setSelectedTier('premium')}
          popular
        />
        <PricingCard
          tier="gold"
          icon={Crown}
          name="Gold"
          monthly={pricingConfig?.gold?.monthly || 200}
          annual={pricingConfig?.gold?.annual || 2000}
          features={[
            '👑 Gold featured badge',
            '📈 3x search visibility',
            '🤖 +30 AI matching bonus',
            '📊 Premium analytics',
            '🎯 Dedicated account manager',
            '⭐ Top placement guarantee'
          ]}
          selected={selectedTier === 'gold'}
          onSelect={() => setSelectedTier('gold')}
        />
      </div>
      {/* Gig/Request Selection (if not profile) */}
      {targetType === 'gig' && (
        <GigRequestSelector
          type="gig"
          items={myGigs}
          selected={selectedTarget}
          onSelect={setSelectedTarget}
        />
      )}
      {targetType === 'request' && (
        <GigRequestSelector
          type="request"
          items={myRequests}
          selected={selectedTarget}
          onSelect={setSelectedTarget}
        />
      )}
      {/* Checkout Button */}
      <div className="text-center">
        <button
          onClick={handlePurchase}
          disabled={purchasing || (targetType !== 'profile' && !selectedTarget)}
          className="px-12 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-bold text-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {purchasing ? (
            <span className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Processing...
            </span>
          ) : (
            `Subscribe Now - GH₵${getPricing()}`
          )}
        </button>
        <p className="text-sm text-gray-500 mt-4">
          Payment will be deducted from your wallet balance
        </p>
      </div>
    </div>
  );
};

export default FeaturedSubscription;